// Turn a `?page=` query string into a safe zero-based page index.
//
// WHY THIS EXISTS
// ---------------
// Routes used to do `(parseInt(pageIdx, 10) || 0) * pageSize` and hand the
// result straight to the GraphQL `currentIndex` arg, which becomes the OFFSET
// in `LIMIT <offset>,<size>`. Two ways that goes wrong:
//
//   ?page=-1  ->  parseInt gives -1, and -1 is TRUTHY so `|| 0` never fires
//                 ->  LIMIT -10,10  ->  MySQL ER_PARSE_ERROR, page 500s.
//                 Observed 37x/day, driven by a sqlmap run against
//                 /publications/7/tags/:tag?page= (the injection itself never
//                 landed — parseInt strips the payload to an integer — but the
//                 negative it left behind reached SQL).
//
//   ?page=9e9 ->  a huge OFFSET makes MySQL walk every preceding row before
//                 discarding it. Cheap to request, expensive to serve.
//
// WHY THE CAP IS AN OFFSET, AND WHY IT IS THIS NUMBER
// ---------------------------------------------------
// The cost of deep pagination is rows-scanned-then-discarded, and that is
// `page * pageSize`, not `page` — so the offset is the thing worth bounding.
// A page cap would mean something different for every caller's pageSize.
//
// The bound comes from the data, measured 2026-08-27:
//
//   whole `news` table          6,101 rows   ->   611 pages at pageSize 10
//   largest single publication  2,573 rows   ->   258 pages
//   largest single tag          386 rows ("Singapore")  ->  39 pages
//
// No query these routes can build returns more than the whole table, so 10,000
// is already ~64% past the point where every page is empty. It leaves room for
// the archive to keep growing without anyone revisiting this, while keeping the
// worst case MySQL can be asked to scan at 10,000 rows — trivial — instead of
// the unbounded walk an attacker could otherwise request.
export const MAX_OFFSET = 10000;
const DEFAULT_PAGE_SIZE = 10;

// Highest page index that still lands inside MAX_OFFSET for a given pageSize.
export function maxPageFor(pageSize = DEFAULT_PAGE_SIZE) {
  const size = Number.isFinite(pageSize) && pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE;
  return Math.floor(MAX_OFFSET / size);
}

// Clamp to [0, maxPageFor(pageSize)]. Pass the SAME pageSize you page with, so
// the pager's highlighted page and the rows it fetched can never disagree.
export function parsePageIndex(value, pageSize = DEFAULT_PAGE_SIZE) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(n, maxPageFor(pageSize));
}

export function pageOffset(value, pageSize = DEFAULT_PAGE_SIZE) {
  return parsePageIndex(value, pageSize) * pageSize;
}

export default { parsePageIndex, pageOffset, maxPageFor, MAX_OFFSET };
